// src/components/ICTEnquiryManager/IctEnquiryManager.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchIctEnquiries,
  createIctEnquiry,
  updateIctEnquiry,
  deleteIctEnquiry,
} from "../../../store/ictEnquirySlice";

// For the live-search of ICT:
import { fetchICTData } from "../../../store/uiSlice";

import "../../../Styles/Common.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const IctEnquiryManager = () => {
  const dispatch = useDispatch();

  // Enquiries
  const { data: enquiries = [], loading, error } = useSelector(
    (state) => state.ictEnquiry
  );

  // ICT data (for live searching ictName to set ict_id)
  const {
    ictData = [],
    loading: ictLoading,
    error: ictError,
  } = useSelector((state) => ({
    ictData: state.ui.ictData,
    loading: state.ui.loading,
    error: state.ui.error,
  }));

  // Searching & pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Sorting (DEFAULT = latest first by created)
  const [sortBy, setSortBy] = useState("createdDesc");

  // Popup form states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // The fields in the DB
  const [formData, setFormData] = useState({
    id: "",
    ict_id: "",
    eEmail: "",
    eName: "",
    eContact: "",
    eQuery: "",
    eSrc: "website",
    status: "A",
  });

  // For the live search of ICT name => ict_id
  const [ictSearchInput, setIctSearchInput] = useState("");
  const [ictSuggestions, setIctSuggestions] = useState([]);

  // Load data on mount
  useEffect(() => {
    dispatch(fetchIctEnquiries());
    dispatch(fetchICTData());
  }, [dispatch]);

  // Show any errors as a toast
  useEffect(() => {
    if (error) toast.error(error);
    if (ictError) toast.error(ictError);
  }, [error, ictError]);

  // Reset to page 1 when filters/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  // CREATE / EDIT form popup handling
  const handleAdd = () => {
    setEditMode(false);
    setIsFormOpen(true);
    setFormData({
      id: "",
      ict_id: "",
      eEmail: "",
      eName: "",
      eContact: "",
      eQuery: "",
      eSrc: "website",
      status: "A",
    });
    setIctSearchInput("");
    setIctSuggestions([]);
  };

  const handleEdit = (record) => {
    setEditMode(true);
    setIsFormOpen(true);

    const ictRec = ictData.find((ict) => ict.id === record.ict_id);
    setIctSearchInput(ictRec ? ictRec.ictName : "");

    setFormData({
      id: record.id,
      ict_id: record.ict_id || "",
      eEmail: record.eEmail || "",
      eName: record.eName || "",
      eContact: record.eContact || "",
      eQuery: record.eQuery || "",
      eSrc: record.eSrc || "website",
      status: record.status || "A",
    });
  };

  // Basic input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Status toggle
  const handleStatusToggle = (checked) => {
    const newStatus = checked ? "A" : "D";
    setFormData((prev) => ({ ...prev, status: newStatus }));
  };

  // Submit create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await dispatch(updateIctEnquiry(formData)).unwrap();
        toast.success("Enquiry updated successfully!");
      } else {
        await dispatch(createIctEnquiry(formData)).unwrap();
        toast.success("Enquiry created successfully!");
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error("Operation failed.");
    }
  };

  // Delete
  const handleDelete = async (record) => {
    if (window.confirm("Are you sure you want to delete this enquiry?")) {
      try {
        await dispatch(deleteIctEnquiry(record.id)).unwrap();
        toast.success("Enquiry deleted successfully!");
      } catch (err) {
        toast.error("Failed to delete enquiry.");
      }
    }
  };

  // Searching: we check eName, eContact, eEmail
  const filteredData = enquiries.filter((enq) => {
    const term = searchTerm.toLowerCase();
    return (
      (enq.eName || "").toLowerCase().includes(term) ||
      (enq.eContact || "").toLowerCase().includes(term) ||
      (enq.eEmail || "").toLowerCase().includes(term)
    );
  });

  // Sorting: keep name sorting + add created sorting (DEFAULT latest)
  const sortedData = [...filteredData].sort((a, b) => {
    const aTime = a?.created ? new Date(a.created).getTime() : 0;
    const bTime = b?.created ? new Date(b.created).getTime() : 0;

    if (sortBy === "createdDesc") {
      return bTime - aTime; // latest first
    }

    if (sortBy === "createdAsc") {
      return aTime - bTime; // oldest first
    }

    if (sortBy === "nameAsc") {
      return (a?.eName || "").localeCompare(b?.eName || "");
    }

    if (sortBy === "nameDesc") {
      return (b?.eName || "").localeCompare(a?.eName || "");
    }

    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => setCurrentPage(page);

  // Live search for ICT:
  const handleIctSearchChange = (e) => {
    const value = e.target.value;
    setIctSearchInput(value);
    if (!value.trim()) {
      setIctSuggestions([]);
    } else {
      const suggestions = ictData.filter((ict) =>
        (ict.ictName || "").toLowerCase().includes(value.toLowerCase())
      );
      setIctSuggestions(suggestions);
    }
  };

  const handleSelectIctSuggestion = (ict) => {
    setFormData((prev) => ({ ...prev, ict_id: ict.id }));
    setIctSearchInput(ict.ictName);
    setIctSuggestions([]);
  };

  return (
    <div className="container">
      <div className="header-container">
        <h2>ICT Enquiry Manager</h2>
        <button className="add-lead-btn" onClick={handleAdd}>
          <i className="fa fa-plus"></i>
        </button>
      </div>

      {/* Searching / Sorting */}
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by Name, Contact, or Email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="filter-input"
          style={{ marginLeft: "1rem" }}
        >
          <option value="createdDesc">Sort by Created (Latest First)</option>
          <option value="createdAsc">Sort by Created (Oldest First)</option>
          <option value="nameAsc">Sort by Name (A-Z)</option>
          <option value="nameDesc">Sort by Name (Z-A)</option>
        </select>
      </div>

      {(loading || ictLoading) && <p>Loading data...</p>}
      {(error || ictError) && <p className="error">{error || ictError}</p>}

      {/* Table */}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>ICT ID</th>
            <th>Name</th>
            <th>Contact</th>
            <th>Email</th>
            <th>Query</th>
            <th>Source</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((rec, index) => (
            <tr key={rec.id}>
              <td>{indexOfFirstItem + index + 1}</td>
              <td>{rec.ict_id}</td>
              <td>{rec.eName}</td>
              <td>{rec.eContact}</td>
              <td>{rec.eEmail}</td>
              <td>{rec.eQuery}</td>
              <td>{rec.eSrc}</td>
              <td>{rec.status}</td>
              <td>{rec.created ? new Date(rec.created).toLocaleString() : ""}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="icon-btn update-btn" onClick={() => handleEdit(rec)}>
                  <i className="fa fa-edit" aria-hidden="true"></i>
                </button>
                {!isReadOnly && (
                  <button
                    className="icon-btn delete-btn"
                    onClick={() => handleDelete(rec)}
                    style={{ marginLeft: "0.5rem" }}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                )}
              </td>
            </tr>
          ))}

          {currentItems.length === 0 && (
            <tr>
              <td colSpan={10} style={{ textAlign: "center" }}>
                No enquiries found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Insert/Edit Form Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Enquiry" : "Add Enquiry"}</h3>
            <form onSubmit={handleSubmit}>
              {/* Live search for ICT Name -> ict_id */}
              <div className="form-group">
                <label>ICT Name (Search):</label>
                <input
                  type="text"
                  placeholder="Search ICT name"
                  value={ictSearchInput}
                  onChange={handleIctSearchChange}
                  required
                />
                {ictSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {ictSuggestions.map((ict) => (
                      <div
                        key={ict.id}
                        className="suggestion-item"
                        onClick={() => handleSelectIctSuggestion(ict)}
                      >
                        {ict.ictName}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>ICT ID (Auto-filled):</label>
                <input
                  type="number"
                  name="ict_id"
                  value={formData.ict_id}
                  onChange={handleChange}
                  required
                  readOnly
                  style={{ background: "#f0f0f0" }}
                />
              </div>

              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  name="eName"
                  value={formData.eName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Contact:</label>
                <input
                  type="number"
                  name="eContact"
                  value={formData.eContact}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="eEmail"
                  value={formData.eEmail}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Query:</label>
                <textarea name="eQuery" value={formData.eQuery} onChange={handleChange} />
              </div>

              <div className="form-group">
                <label>Source:</label>
                <select name="eSrc" value={formData.eSrc} onChange={handleChange}>
                  <option value="website">Website</option>
                  <option value="email">Email</option>
                  <option value="socialmedia">Social Media</option>
                  <option value="google">Google</option>
                  <option value="csr">CSR</option>
                  <option value="refer">Refer</option>
                </select>
              </div>

              {/* Switch for status */}
              <div className="form-group">
                <label>Status:</label>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.status === "A"}
                    onChange={(e) => handleStatusToggle(e.target.checked)}
                  />
                  <span className="slider round"></span>
                </label>
                <span style={{ marginLeft: "0.5rem" }}>
                  {formData.status === "A" ? "Active" : "Deactivated"}
                </span>
              </div>

              <div className="form-actions">
                <button type="submit" className="fa fa-check submit-icon"></button>
                <button
                  type="button"
                  className="fa fa-times cancel-icon"
                  onClick={() => setIsFormOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default IctEnquiryManager;
